/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { createCopiedToast } from "components/toasts/Toasts";
import React, { FunctionComponent, useCallback, useEffect, useState } from "react";
import { PanelState } from "types";
import { Anchor, Button, Dialog, Fieldset, InputGroup, Textarea, ToggleSwitch } from "@itwin/itwinui-react";
import styles from "./EmbedDialog.module.css";

interface EmbedDialogProps {
  codeShare: string;
  username: string;
  onClose: () => void;
}

export const EmbedDialog: FunctionComponent<EmbedDialogProps> = ({ codeShare, username, onClose }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [includePreviewPane, setIncludePreviewPane] = useState(true);
  const [openPreviewPane, setOpenPreviewPane] = useState(true);
  const [includeEditorPane, setIncludeEditorPane] = useState(true);
  const [openEditorPane, setOpenEditorPane] = useState(true);
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [embedString, setEmbedString] = useState("");

  useEffect(() => {
    setTimeout(() => setIsModalOpen(true), 5);
  }, []);

  useEffect(() => {
    let url = encodeURI(`${window.location.origin}/sandbox/${username}/${codeShare}`);
    const options: Array<{ option: string, value: PanelState }> = [];

    const getPaneState = (include: boolean, open: boolean): PanelState => {
      return include ? (open ? "open" : "closed") : "hidden";
    };

    const previewPane = getPaneState(includePreviewPane, openPreviewPane);
    if ("open" !== previewPane)
      options.push({ option: "previewPane", value: previewPane });

    const editorPane = getPaneState(includeEditorPane, openEditorPane);
    if ("open" !== editorPane)
      options.push({ option: "editorPane", value: editorPane });

    const headersState = getPaneState(includeHeaders, true);
    if ("open" !== headersState)
      options.push({ option: "headers", value: headersState });

    const params = new URLSearchParams();
    params.append("mainHeader", "hidden");

    options.forEach((item) => {
      params.append(item.option, item.value)
    });

    url = params.toString() ? `${url}?${params.toString()}` : url;
    const newString = `<iframe src="${url}" style="width:100%;height:500px;border:0;overflow:hidden" sandbox="allow-forms allow-modals allow-popups allow-presentation allow-scripts"></iframe>`;
    setEmbedString(newString);

  }, [username, codeShare, includePreviewPane, openPreviewPane, includeEditorPane, openEditorPane, includeHeaders]);

  const copyToClipboard = useCallback(async (event: React.MouseEvent) => {
    event.stopPropagation();
    await navigator.clipboard.writeText(embedString);
    createCopiedToast();
  }, [embedString]);

  const previewSwitchOnChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.checked)
      setOpenEditorPane(true);

    setIncludePreviewPane(event.target.checked);
  }, []);

  const editorSwitchOnChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.checked)
      setOpenPreviewPane(true);

    setIncludeEditorPane(event.target.checked);
  }, []);

  const onModalClose = () => {
    setIsModalOpen(false)
    setTimeout(onClose, 200);
  }

  return (
    <Dialog isOpen={isModalOpen} onClose={onModalClose} closeOnEsc closeOnExternalClick preventDocumentScroll trapFocus setFocus isDismissible>
      <Dialog.Backdrop />
      <Dialog.Main data-testid="atp-embed-sandbox-dialog">
        <Dialog.TitleBar titleText="Embed Sandbox" data-testid="atp-embed-sandbox-title"/>
        <Dialog.Content>
          <p>
            Please insert the following HTML snippet in your site to display
            contents of current sandbox
          </p>
          <div className="form-group">
            <div className="copy-icon">
              <Anchor onClick={copyToClipboard}><i className="icon icon-clipboard-copy"></i></Anchor>
            </div>
            <Textarea className={styles.embedString} value={embedString} spellCheck={false} readOnly />
          </div>

          <Fieldset legend="Layout settings" className={styles.options}>
            <InputGroup label="Preview pane" displayStyle="inline" className={styles.toggle}>
              <ToggleSwitch label="Include" checked={includePreviewPane} onChange={previewSwitchOnChange} disabled={!includeEditorPane} />
              <ToggleSwitch label="Expand" checked={openPreviewPane} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setOpenPreviewPane(event.target.checked)} disabled={!includePreviewPane || !openEditorPane || !includeEditorPane} />
            </InputGroup>
            <InputGroup label="Editor pane" displayStyle="inline" className={styles.toggle}>
              <ToggleSwitch label="Include" checked={includeEditorPane} onChange={editorSwitchOnChange} disabled={!includePreviewPane} />
              <ToggleSwitch label="Expand" checked={openEditorPane} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setOpenEditorPane(event.target.checked)} disabled={!includeEditorPane || !openPreviewPane || !includePreviewPane} />
            </InputGroup>
            <InputGroup label="Headers for Editor and Preview panes" displayStyle="inline" className={styles.toggle}>
              <ToggleSwitch label="Include" checked={includeHeaders} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setIncludeHeaders(event.target.checked)} />
            </InputGroup>
          </Fieldset>
        </Dialog.Content>
        <Dialog.ButtonBar>
          <Button onClick={onModalClose} data-testid="atp-embed-close">Close</Button>
        </Dialog.ButtonBar>
      </Dialog.Main>
    </Dialog>
  )
};
